import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, TrendingUp, DollarSign, Package, BarChart3, FileText, RefreshCw } from 'lucide-react';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import PDFDownloadButton from '@/components/PDFDownloadButton';
import { api } from '@/services/api';

interface SalesReport {
  summary: {
    totalSales: number;
    totalRevenue: number;
    totalTax: number;
    totalSubtotal: number;
    averageSale: number;
  };
  paymentBreakdown: Record<string, { count: number; total: number }>;
  mraStatusBreakdown: Record<string, { count: number; total: number }>;
  dailySales: Array<{
    date: string;
    sales: number;
    revenue: number;
    tax: number;
  }>;
  topProducts: Array<{
    productId: string;
    name: string;
    quantity: number;
    revenue: number;
  }>;
}

interface VATReport {
  summary: {
    totalExcludingVAT: number;
    totalVAT: number;
    totalIncludingVAT: number;
    effectiveVATRate: number;
    invoiceCount: number;
  };
  invoices: Array<{
    invoiceNumber: string;
    invoiceDate: string;
    totalExcludingVAT: number;
    vatAmount: number;
    totalIncludingVAT: number;
    mraStatus: string;
    mraInvoiceId?: string;
  }>;
  businessInfo: {
    name: string;
    tpin: string;
    address: string;
    phone: string;
    email: string;
  };
}

interface InventoryReport {
  summary: {
    totalProducts: number;
    activeProducts: number;
    totalStockValue: number;
    lowStockProducts: number;
    outOfStockProducts: number;
  };
  categories: Record<string, {
    count: number;
    totalValue: number;
    lowStock: number;
  }>;
  products: Array<{
    id: string;
    sku: string;
    name: string;
    category: string;
    price: number;
    cost?: number;
    stockQuantity: number;
    stockValue: number;
    isActive: boolean;
    lowStock: boolean;
    outOfStock: boolean;
    profitMargin: number;
  }>;
}

const ReportsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'sales' | 'vat' | 'inventory'>('sales');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 days ago
    endDate: new Date().toISOString().split('T')[0], // today
  });
  const [groupBy, setGroupBy] = useState<'day' | 'week' | 'month'>('day');
  const queryClient = useQueryClient();

  // Sales report query
  const { data: salesReport, isLoading: isLoadingSales } = useQuery<SalesReport>({
    queryKey: ['reports', 'sales', dateRange, groupBy],
    queryFn: async () => {
      const res = await api.get('/api/reports/sales', {
        params: { ...dateRange, groupBy }
      });
      return res.data?.data?.report || res.data?.report;
    },
  });

  // VAT report query
  const { data: vatReport, isLoading: isLoadingVAT } = useQuery<VATReport>({
    queryKey: ['reports', 'vat', dateRange],
    queryFn: async () => {
      const res = await api.get('/api/reports/vat', {
        params: dateRange
      });
      return res.data?.data?.report || res.data?.report;
    },
  });

  // Inventory report query
  const { data: inventoryReport, isLoading: isLoadingInventory } = useQuery<InventoryReport>({
    queryKey: ['reports', 'inventory'],
    queryFn: async () => {
      const res = await api.get('/api/reports/inventory');
      return res.data?.data?.report || res.data?.report;
    },
  });

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-MW', {
      style: 'currency',
      currency: 'MWK',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-MW', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const isLoading = activeTab === 'sales' ? isLoadingSales : activeTab === 'vat' ? isLoadingVAT : isLoadingInventory;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
          <p className="mt-1 text-sm text-gray-600">
            Generate and download comprehensive business reports
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant="ghost"
            onClick={() => {
              // Refresh all report queries
              queryClient.invalidateQueries({ queryKey: ['reports'] });
            }}
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          {activeTab === 'sales' && (
            <PDFDownloadButton
              endpoint={`/enhanced-reports/sales/pdf?dateRange=${dateRange}`}
              filename={`sales-report-${new Date().toISOString().split('T')[0]}.pdf`}
              variant="primary"
            >
              <Download className="h-4 w-4 mr-2" />
              Download Sales Report
            </PDFDownloadButton>
          )}
          {activeTab === 'vat' && (
            <PDFDownloadButton
              endpoint={`/enhanced-reports/vat/pdf?dateRange=${dateRange}`}
              filename={`vat-report-${new Date().toISOString().split('T')[0]}.pdf`}
              variant="primary"
            >
              <Download className="h-4 w-4 mr-2" />
              Download VAT Report
            </PDFDownloadButton>
          )}
          {activeTab === 'inventory' && (
            <PDFDownloadButton
              endpoint="/enhanced-reports/inventory/pdf"
              filename={`inventory-report-${new Date().toISOString().split('T')[0]}.pdf`}
              variant="primary"
            >
              <Download className="h-4 w-4 mr-2" />
              Download Inventory Report
            </PDFDownloadButton>
          )}
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <Input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <Input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              />
            </div>
            {activeTab === 'sales' && (
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Group By</label>
                <select
                  value={groupBy}
                  onChange={(e) => setGroupBy(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="day">Day</option>
                  <option value="week">Week</option>
                  <option value="month">Month</option>
                </select>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'sales', label: 'Sales Analytics', icon: BarChart3 },
            { id: 'vat', label: 'VAT Report', icon: FileText },
            { id: 'inventory', label: 'Inventory', icon: Package },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <tab.icon className="h-4 w-4 mr-2" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Report Content */}
      {isLoading ? (
        <div className="card">
          <div className="card-body text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
            <p className="mt-2 text-sm text-gray-600">Loading report...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Sales Report */}
          {activeTab === 'sales' && salesReport && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="card">
                  <div className="card-body">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 bg-blue-100 rounded-lg p-3">
                        <FileText className="h-6 w-6 text-blue-600" />
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Total Sales</p>
                        <p className="text-2xl font-bold text-gray-900">{salesReport.summary.totalSales}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card">
                  <div className="card-body">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 bg-green-100 rounded-lg p-3">
                        <DollarSign className="h-6 w-6 text-green-600" />
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Revenue</p>
                        <p className="text-2xl font-bold text-gray-900">{formatCurrency(salesReport.summary.totalRevenue)}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card">
                  <div className="card-body">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 bg-purple-100 rounded-lg p-3">
                        <FileText className="h-6 w-6 text-purple-600" />
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">VAT Collected</p>
                        <p className="text-2xl font-bold text-gray-900">{formatCurrency(salesReport.summary.totalTax)}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card">
                  <div className="card-body">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 bg-orange-100 rounded-lg p-3">
                        <TrendingUp className="h-6 w-6 text-orange-600" />
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Average Sale</p>
                        <p className="text-2xl font-bold text-gray-900">{formatCurrency(salesReport.summary.averageSale)}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sales Chart Placeholder */}
              <div className="card">
                <div className="card-header">
                  <h3 className="text-lg font-medium text-gray-900">Sales Trend</h3>
                </div>
                <div className="card-body">
                  <div className="h-64 bg-gray-50 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                      <BarChart3 className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Chart visualization would be implemented here</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Top Products */}
              <div className="card">
                <div className="card-header">
                  <h3 className="text-lg font-medium text-gray-900">Top Products</h3>
                </div>
                <div className="card-body">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Quantity</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Revenue</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {salesReport.topProducts.map((product: any) => (
                          <tr key={product.productId}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {product.name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {product.quantity}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatCurrency(product.revenue)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* VAT Report */}
          {activeTab === 'vat' && vatReport && (
            <div className="space-y-6">
              {/* VAT Summary */}
              <div className="card">
                <div className="card-header">
                  <h3 className="text-lg font-medium text-gray-900">VAT Summary</h3>
                </div>
                <div className="card-body">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Business Information</h4>
                      <div className="space-y-1 text-sm">
                        <p><span className="font-medium">Name:</span> {vatReport.businessInfo.name}</p>
                        <p><span className="font-medium">TPIN:</span> {vatReport.businessInfo.tpin}</p>
                        <p><span className="font-medium">Address:</span> {vatReport.businessInfo.address}</p>
                        <p><span className="font-medium">Phone:</span> {vatReport.businessInfo.phone}</p>
                        <p><span className="font-medium">Email:</span> {vatReport.businessInfo.email}</p>
                      </div>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">VAT Totals</h4>
                      <div className="space-y-1 text-sm">
                        <p><span className="font-medium">Total Excluding VAT:</span> {formatCurrency(vatReport.summary.totalExcludingVAT)}</p>
                        <p><span className="font-medium">VAT Amount:</span> {formatCurrency(vatReport.summary.totalVAT)}</p>
                        <p><span className="font-medium">Total Including VAT:</span> {formatCurrency(vatReport.summary.totalIncludingVAT)}</p>
                        <p><span className="font-medium">VAT Rate:</span> {vatReport.summary.effectiveVATRate}%</p>
                        <p><span className="font-medium">Invoice Count:</span> {vatReport.summary.invoiceCount}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* VAT Invoices */}
              <div className="card">
                <div className="card-header">
                  <h3 className="text-lg font-medium text-gray-900">VAT Invoices</h3>
                </div>
                <div className="card-body">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Excl. VAT</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">VAT</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">MRA Status</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {vatReport.invoices.map((invoice) => (
                          <tr key={invoice.invoiceNumber}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {invoice.invoiceNumber}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatDate(invoice.invoiceDate)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatCurrency(invoice.totalExcludingVAT)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatCurrency(invoice.vatAmount)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatCurrency(invoice.totalIncludingVAT)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                invoice.mraStatus === 'approved' ? 'bg-green-100 text-green-800' :
                                invoice.mraStatus === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-red-100 text-red-800'
                              }`}>
                                {invoice.mraStatus}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Inventory Report */}
          {activeTab === 'inventory' && inventoryReport && (
            <div className="space-y-6">
              {/* Inventory Summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="card">
                  <div className="card-body">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 bg-blue-100 rounded-lg p-3">
                        <Package className="h-6 w-6 text-blue-600" />
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Total Products</p>
                        <p className="text-2xl font-bold text-gray-900">{inventoryReport.summary.totalProducts}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card">
                  <div className="card-body">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 bg-green-100 rounded-lg p-3">
                        <Package className="h-6 w-6 text-green-600" />
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Active</p>
                        <p className="text-2xl font-bold text-gray-900">{inventoryReport.summary.activeProducts}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card">
                  <div className="card-body">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 bg-yellow-100 rounded-lg p-3">
                        <Package className="h-6 w-6 text-yellow-600" />
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Low Stock</p>
                        <p className="text-2xl font-bold text-gray-900">{inventoryReport.summary.lowStockProducts}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card">
                  <div className="card-body">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 bg-red-100 rounded-lg p-3">
                        <Package className="h-6 w-6 text-red-600" />
                      </div>
                      <div className="ml-4">
                        <p className="text-sm font-medium text-gray-600">Out of Stock</p>
                        <p className="text-2xl font-bold text-gray-900">{inventoryReport.summary.outOfStockProducts}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Inventory Table */}
              <div className="card">
                <div className="card-header">
                  <h3 className="text-lg font-medium text-gray-900">Product Inventory</h3>
                </div>
                <div className="card-body">
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Price</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stock</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Stock Value</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Margin</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {inventoryReport.products.map((product) => (
                          <tr key={product.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {product.name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {product.sku}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {product.category}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatCurrency(product.price)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                product.outOfStock ? 'bg-red-100 text-red-800' :
                                product.lowStock ? 'bg-yellow-100 text-yellow-800' :
                                'bg-green-100 text-green-800'
                              }`}>
                                {product.stockQuantity}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatCurrency(product.stockValue)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {product.profitMargin.toFixed(1)}%
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                product.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {product.isActive ? 'Active' : 'Inactive'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReportsPage;
